import React, { useState, useEffect } from "react";
import { getData, putData } from "../../../FetchApiServices";
import { useToast } from "../Layout/T2TUIProvider";

// BUG-18 — UI now works in MINUTES.
// DB column is still sla_hours (fractional), so we convert at the boundary:
//   • Display: hours × 60 → minutes  (auto-format < 60 → "Nm", ≥ 60 → "Hh Mm")
//   • Save:    minutes ÷ 60 → hours  (sent to backend in PUT body)
// Cron + escalation logic on the backend is untouched.

const minutesToHours = (m) => (Number(m) || 0) / 60;
const hoursToMinutes = (h) => Math.round((Number(h) || 0) * 60);
const formatHoursAsHM = (h) => {
  if (h == null || h === "" || Number(h) === 0) return "No SLA";
  const totalMin = hoursToMinutes(h);
  if (totalMin < 60) return `${totalMin}m`;
  const hh = Math.floor(totalMin / 60);
  const mm = totalMin % 60;
  return mm === 0 ? `${hh}h` : `${hh}h ${mm}m`;
};

const SlaConfigAdmin = () => {
  const toast = useToast();

  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [editing, setEditing]     = useState({}); // { stageId: minutes_string }
  const [saving, setSaving]       = useState({});

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    setLoading(true);
    const resp = await getData("t2t/workflows?page=1&limit=100&active=true");
    if (!resp.error) {
      const raw = resp.data?.data;
      const all = Array.isArray(raw) ? raw
        : Array.isArray(raw?.data) ? raw.data
        : Array.isArray(raw?.rows) ? raw.rows
        : Array.isArray(raw?.workflows) ? raw.workflows
        : [];
      const withStages = await Promise.all(
        all.map(async (wf) => {
          const wr = await getData(`t2t/workflows/${wf.workflow_id}`);
          const wfData = wr.data?.data || wr.data || {};
          return { ...wf, stages: wfData.stages || [] };
        })
      );
      setWorkflows(withStages);
    }
    setLoading(false);
  };

  // BUG-18: start edit pre-fills MINUTES (converted from stored fractional hours).
  const startEdit = (stageId, currentHours) => {
    const mins = currentHours != null ? String(hoursToMinutes(currentHours)) : "";
    setEditing((prev) => ({ ...prev, [stageId]: mins }));
  };

  const cancelEdit = (stageId) => {
    setEditing((prev) => { const n = { ...prev }; delete n[stageId]; return n; });
  };

  const save = async (wfId, stageId, stageName) => {
    const mins = parseFloat(editing[stageId]);
    if (isNaN(mins) || mins < 0) { toast("Enter a valid number of minutes (0 = no SLA).", "error"); return; }
    setSaving((prev) => ({ ...prev, [stageId]: true }));
    // BUG-18: send only the two fields the validator allows.
    // Spreading the whole stage row used to trip Joi with "stage_id is not allowed".
    const resp = await putData(`t2t/workflows/${wfId}/stages/${stageId}`, {
      name:      stageName,
      sla_hours: mins > 0 ? minutesToHours(mins) : null,
    });
    setSaving((prev) => { const n = { ...prev }; delete n[stageId]; return n; });
    if (resp.error) { toast(resp.error.message || "Save failed.", "error"); return; }
    toast("SLA updated.", "success");
    cancelEdit(stageId);
    load();
  };

  if (loading) return <div style={{ fontSize: 12, color: "var(--t2t-text-muted)", padding: 16 }}>Loading workflows…</div>;

  if (workflows.length === 0) return (
    <div style={{ fontSize: 12, color: "var(--t2t-text-muted)", padding: 16 }}>
      No active workflows found. Create and activate workflows in the Workflow Builder first.
    </div>
  );

  return (
    <div>
      {/* BUG-18: clarifying banner — same data as Workflow Builder, no duplication */}
      <div style={{
        padding: "10px 12px",
        background: "#EFF6FF",
        border: "1px solid #BFDBFE",
        borderRadius: 6,
        fontSize: 12,
        color: "#1E3A8A",
        marginBottom: 12,
      }}>
        <strong>This grid is a shortcut.</strong> It edits the same per-stage SLA you configure in the Workflow Builder — changes here are reflected everywhere immediately.
      </div>

      <p style={{ fontSize: 12, color: "var(--t2t-text-secondary)", marginBottom: 16 }}>
        SLA minutes are enforced per stage. At 80% elapsed the stage assignee receives a warning.
        At 100% they receive a breach notice. At 150% their manager is escalated.
        Set to 0 or blank to disable SLA for a stage.
      </p>

      {workflows.map((wf) => (
        <div key={wf.workflow_id} style={{ marginBottom: 24, border: "1px solid #E2E8F0", borderRadius: 8, overflow: "hidden" }}>
          <div style={{ padding: "10px 14px", background: "#F8FAFC", borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontWeight: 700, fontSize: 13 }}>{wf.name || wf.workflow_name}</span>
            {(wf.request_type_name || wf.request_type?.name) && (
              <span style={{ fontSize: 10, color: "#64748B", background: "#E2E8F0", padding: "1px 8px", borderRadius: 4 }}>
                {wf.request_type_name || wf.request_type?.name}
              </span>
            )}
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #F1F5F9" }}>
                {["Stage", "Type", "SLA (minutes)", ""].map((h) => (
                  <th key={h} style={{ padding: "7px 12px", textAlign: "left", fontWeight: 600, color: "#475569", fontSize: 11 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(wf.stages || []).filter((s) => !s.parent_stage_id).map((s) => {
                const stageId = s.stage_id || s.id;
                const isEditing = stageId in editing;
                return (
                  <tr key={stageId} style={{ borderBottom: "1px solid #F9FAFB" }}>
                    <td style={{ padding: "8px 12px", fontWeight: 500 }}>{s.name}</td>
                    <td style={{ padding: "8px 12px", color: "#64748B" }}>{s.stage_type || "approval"}</td>
                    <td style={{ padding: "8px 12px" }}>
                      {isEditing ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <input
                            type="number"
                            min="0"
                            step="15"
                            value={editing[stageId]}
                            onChange={(e) => setEditing((prev) => ({ ...prev, [stageId]: e.target.value }))}
                            style={{ width: 90, padding: "3px 6px", border: "1px solid #CBD5E1", borderRadius: 4, fontSize: 12 }}
                            placeholder="minutes"
                          />
                          <span style={{ fontSize: 11, color: "#94A3B8" }}>min</span>
                        </div>
                      ) : (
                        <span style={{ color: s.sla_hours ? "#0F172A" : "#94A3B8" }}>
                          {formatHoursAsHM(s.sla_hours)}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: "8px 12px", textAlign: "right" }}>
                      {isEditing ? (
                        <>
                          <button
                            className="t2t-btn t2t-btn-primary t2t-btn-sm"
                            disabled={saving[stageId]}
                            onClick={() => save(wf.workflow_id, stageId, s.name)}
                            style={{ marginRight: 6 }}
                          >
                            {saving[stageId] ? "…" : "Save"}
                          </button>
                          <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={() => cancelEdit(stageId)}>Cancel</button>
                        </>
                      ) : (
                        <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={() => startEdit(stageId, s.sla_hours)}>
                          ✎ Edit
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

export default SlaConfigAdmin;
